import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProgress } from '../contexts/ProgressContext';
import { 
    getLessonById, 
    getSectionById, 
    getNextLesson,
    getPreviousLesson,
    lessons,
    sections
} from '../data/courseData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    BookOpen, 
    Compass, 
    Flame, 
    Sparkles, 
    ArrowLeft,
    Star,
    ChevronRight,
    ChevronLeft,
    Check,
    RotateCcw,
    Menu,
    X,
    Home
} from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";

const iconMap = {
    'book-open': BookOpen,
    'compass': Compass,
    'flame': Flame,
    'sparkles': Sparkles
};

const colorClasses = {
    primary: {
        bg: 'bg-primary/10',
        border: 'border-primary/30',
        text: 'text-primary'
    },
    mystical: {
        bg: 'bg-mystical/10',
        border: 'border-mystical/30',
        text: 'text-mystical-glow'
    },
    accent: {
        bg: 'bg-accent/10',
        border: 'border-accent/30',
        text: 'text-accent-glow'
    },
    emerald: {
        bg: 'bg-emerald/10',
        border: 'border-emerald/30',
        text: 'text-emerald'
    }
};

const LessonPage = () => {
    const { lessonId } = useParams();
    const navigate = useNavigate();
    const { isLessonComplete, markLessonComplete, markLessonIncomplete } = useProgress();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [embedLoaded, setEmbedLoaded] = useState(false);
    const currentLessonRef = useRef(null);

    const lesson = getLessonById(lessonId);
    const section = lesson ? getSectionById(lesson.sectionId) : null;
    
    // Get next and previous lessons across the ENTIRE course (not just section)
    const prevLesson = lesson ? getPreviousLesson(lesson.id) : null;
    const nextLesson = lesson ? getNextLesson(lesson.id) : null;
    
    const isComplete = lesson ? isLessonComplete(lesson.id) : false;

    // Find current lesson index in the full course
    const currentLessonIndex = lessons.findIndex(l => l.id === lessonId);

    useEffect(() => {
        setEmbedLoaded(false);
    }, [lessonId]);

    // Scroll to current lesson in sidebar when it changes
    useEffect(() => {
        if (currentLessonRef.current) {
            currentLessonRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
            });
        }
    }, [lessonId]);

    if (!lesson || !section) {
        return (
            <div className="min-h-screen relative flex items-center justify-center">
                <div 
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="fixed inset-0 bg-background/80" />
                <Card className="bg-card/80 backdrop-blur-sm relative z-10">
                    <CardContent className="p-8 text-center">
                        <h2 className="font-display text-xl text-foreground mb-4">Lesson Not Found</h2>
                        <Button variant="gold" onClick={() => navigate('/dashboard')}>
                            Return to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const Icon = iconMap[section.icon] || BookOpen;
    const colors = colorClasses[section.color] || colorClasses.primary;

    const handleComplete = () => {
        markLessonComplete(lesson.id);
    };

    const handleReturn = () => {
        navigate(`/section/${section.id}`);
    };

    const handleToggleComplete = () => {
        if (isComplete) {
            markLessonIncomplete(lesson.id);
        } else {
            markLessonComplete(lesson.id);
        }
    };

    // Group lessons by section for the sidebar
    const lessonsBySection = sections.map(sec => ({
        section: sec,
        lessons: lessons.filter(l => l.sectionId === sec.id)
    }));

    return (
        <div className="min-h-screen relative flex">
            {/* Background image */}
            <div 
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            <div className="fixed inset-0 bg-background/85" />
            
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar - Full Course Table of Contents */}
            <aside className={`
                fixed lg:sticky top-0 left-0 z-50 lg:z-40
                w-80 h-screen bg-card/95 lg:bg-card/70 backdrop-blur-sm
                border-r border-border/50
                transform transition-transform duration-300 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Sidebar Header */}
                    <div className="p-4 border-b border-border/50">
                        <div className="flex items-center justify-between mb-3">
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate('/dashboard')}
                                data-testid="sidebar-dashboard-btn"
                            >
                                <Home className="w-4 h-4" />
                                Dashboard
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                onClick={() => setSidebarOpen(false)}
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                        <div className="text-center">
                            <p className="font-display text-sm text-foreground">Course Progress</p>
                            <p className="font-ui text-xs text-muted-foreground">
                                Lesson {currentLessonIndex + 1} of {lessons.length}
                            </p>
                        </div>
                    </div>

                    {/* Full Course Lessons List */}
                    <ScrollArea className="flex-1">
                        <div className="p-3 space-y-4">
                            {lessonsBySection.map(({ section: sec, lessons: secLessons }) => {
                                const SectionIcon = iconMap[sec.icon] || BookOpen;
                                const sectionColors = colorClasses[sec.color] || colorClasses.primary;
                                const isCurrentSection = sec.id === section.id;

                                return (
                                    <div key={sec.id}>
                                        {/* Section Header */}
                                        <div className={`
                                            flex items-center gap-2 mb-2 p-2 rounded-lg
                                            ${isCurrentSection ? sectionColors.bg + ' border ' + sectionColors.border : ''}
                                        `}>
                                            <div className={`p-1.5 rounded ${sectionColors.bg} border ${sectionColors.border}`}>
                                                <SectionIcon className={`w-3 h-3 ${sectionColors.text}`} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`font-display text-xs font-medium truncate ${isCurrentSection ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                    {sec.number}. {sec.title}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Section Lessons */}
                                        <div className="space-y-0.5 ml-2">
                                            {secLessons.map((secLesson, idx) => {
                                                const isCurrent = secLesson.id === lesson.id;
                                                const lessonComplete = isLessonComplete(secLesson.id);
                                                const globalIndex = lessons.findIndex(l => l.id === secLesson.id);

                                                return (
                                                    <button
                                                        key={secLesson.id}
                                                        ref={isCurrent ? currentLessonRef : null}
                                                        onClick={() => {
                                                            navigate(`/lesson/${secLesson.id}`);
                                                            setSidebarOpen(false);
                                                        }}
                                                        data-testid={`sidebar-lesson-${secLesson.id}`}
                                                        className={`
                                                            w-full text-left py-2 px-3 rounded-lg transition-all duration-200
                                                            flex items-center gap-2
                                                            ${isCurrent 
                                                                ? 'bg-primary/20 border border-primary/30' 
                                                                : 'hover:bg-card-elevated border border-transparent hover:border-border/50'
                                                            }
                                                        `}
                                                    >
                                                        <div className={`
                                                            w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-xs
                                                            ${lessonComplete 
                                                                ? 'bg-primary/20' 
                                                                : 'bg-muted'
                                                            }
                                                        `}>
                                                            {lessonComplete ? (
                                                                <Star className="w-3 h-3 text-primary fill-primary" />
                                                            ) : (
                                                                <span className="font-ui text-[10px] text-muted-foreground">
                                                                    {globalIndex + 1}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className={`
                                                            font-ui text-xs truncate
                                                            ${isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground'}
                                                        `}>
                                                            {secLesson.title}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>

                    {/* Sidebar Footer - Navigation across entire course */}
                    <div className="p-3 border-t border-border/50 space-y-2">
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                disabled={!prevLesson}
                                onClick={() => prevLesson && navigate(`/lesson/${prevLesson.id}`)}
                                data-testid="prev-lesson-btn"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Prev
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                disabled={!nextLesson}
                                onClick={() => nextLesson && navigate(`/lesson/${nextLesson.id}`)}
                                data-testid="next-lesson-btn"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                        <Button
                            variant="codex"
                            size="sm"
                            className="w-full"
                            onClick={handleReturn}
                            data-testid="back-to-section-btn"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Section
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-h-screen relative z-10">
                {/* Top Bar */}
                <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-30">
                    <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                onClick={() => setSidebarOpen(true)}
                                data-testid="open-sidebar-btn"
                            >
                                <Menu className="w-5 h-5" />
                            </Button>
                            <div className="hidden sm:block">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <div className={`p-1 rounded ${colors.bg} border ${colors.border}`}>
                                        <Icon className={`w-3 h-3 ${colors.text}`} />
                                    </div>
                                    <span className="font-ui text-xs text-muted-foreground">
                                        Section {section.number} • Lesson {currentLessonIndex + 1}/{lessons.length}
                                    </span>
                                </div>
                                <h1 className="font-display text-lg font-semibold text-foreground truncate max-w-md">
                                    {lesson.title}
                                </h1>
                            </div>
                        </div>
                        
                        {/* Completion Toggle */}
                        <Button
                            variant={isComplete ? 'default' : 'outline'}
                            size="sm"
                            onClick={handleToggleComplete}
                            className={isComplete ? 'bg-success hover:bg-success/90' : ''}
                            data-testid="toggle-complete-btn"
                        >
                            {isComplete ? (
                                <>
                                    <Star className="w-4 h-4 fill-current" />
                                    <span className="hidden sm:inline">Completed</span>
                                </>
                            ) : (
                                <>
                                    <Star className="w-4 h-4" />
                                    <span className="hidden sm:inline">Mark Complete</span>
                                </>
                            )}
                        </Button>
                    </div>
                </header>

                {/* Mobile Title */}
                <div className="sm:hidden px-4 py-3 border-b border-border/50">
                    <div className="flex items-center gap-2 mb-1">
                        <div className={`p-1 rounded ${colors.bg} border ${colors.border}`}>
                            <Icon className={`w-3 h-3 ${colors.text}`} />
                        </div>
                        <span className="font-ui text-xs text-muted-foreground">
                            Lesson {currentLessonIndex + 1}/{lessons.length}
                        </span>
                    </div>
                    <h1 className="font-display text-lg font-semibold text-foreground">
                        {lesson.title}
                    </h1>
                    <p className="font-body text-sm text-muted-foreground">
                        {lesson.subtitle}
                    </p>
                </div>

                {/* Gamma Embed */}
                <div className="flex-1 p-4 sm:p-6">
                    <div className="gamma-embed-container h-full min-h-[500px] lg:min-h-[600px] rounded-lg border border-border/50 overflow-hidden relative">
                        {!embedLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-card">
                                <div className="text-center">
                                    <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                    <p className="font-ui text-sm text-muted-foreground">Loading lesson content...</p>
                                </div>
                            </div>
                        )}
                        <iframe
                            src={lesson.embedUrl}
                            style={{ width: '100%', height: '100%', minHeight: '500px' }}
                            allow="fullscreen"
                            title={lesson.title}
                            onLoad={() => setEmbedLoaded(true)}
                        />
                    </div>
                </div>

                {/* Bottom Action Bar */}
                <div className="border-t border-border/50 bg-card/50 backdrop-blur-sm p-4 pb-20 sm:pb-4">
                    <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            {isComplete ? (
                                <div className="flex items-center gap-2 text-success">
                                    <Star className="w-5 h-5 fill-current" />
                                    <span className="font-ui text-sm">Lesson Completed</span>
                                </div>
                            ) : (
                                <p className="font-ui text-sm text-muted-foreground">
                                    Ready to continue?
                                </p>
                            )}
                        </div>
                        
                        <div className="flex items-center gap-3">
                            {prevLesson && (
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/lesson/${prevLesson.id}`)}
                                    data-testid="bottom-prev-btn"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </Button>
                            )}
                            
                            {!isComplete && (
                                <Button
                                    variant="complete"
                                    onClick={handleComplete}
                                    data-testid="complete-lesson-btn"
                                >
                                    <Check className="w-4 h-4" />
                                    Complete Lesson
                                </Button>
                            )}
                            
                            {nextLesson ? (
                                <Button
                                    variant="gold"
                                    onClick={() => navigate(`/lesson/${nextLesson.id}`)}
                                    data-testid="bottom-next-btn"
                                >
                                    Next Lesson
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            ) : (
                                <Button
                                    variant="gold"
                                    onClick={() => navigate('/dashboard')}
                                    data-testid="finish-course-btn"
                                >
                                    <Star className="w-4 h-4" />
                                    Finish Course
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LessonPage;
